import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { MainLayout } from '../layouts/MainLayout';
import { projectService } from '../services/api/projectService';
import type { MAMethod, Country, RiskCategory } from '../types';

const steps = [
  '基本情報',
  'M&A手法',
  '対象企業情報',
  'リスクカテゴリ選択',
  '確認',
];

const maMethodOptions: { value: MAMethod; label: string; description: string }[] = [
  { value: 'stock_transfer', label: '株式譲渡', description: '対象会社の株式を取得する方法' },
  { value: 'business_transfer', label: '事業譲渡', description: '事業の一部または全部を譲り受ける方法' },
  { value: 'merger', label: '合併', description: '2つ以上の会社が1つになる方法' },
  { value: 'company_split', label: '会社分割', description: '会社の事業を分割して承継する方法' },
  { value: 'stock_exchange', label: '株式交換・移転', description: '完全親子会社関係を創設する方法' },
];

const countryOptions: { value: Country; label: string }[] = [
  { value: 'japan', label: '日本' },
  { value: 'usa', label: '米国' },
  { value: 'uk', label: '英国' },
  { value: 'eu', label: 'EU' },
  { value: 'china', label: '中国' },
];

const riskCategoryOptions: { value: RiskCategory; label: string; description: string }[] = [
  { value: 'legal', label: '法的リスク', description: 'COC条項、表明保証、独禁法等' },
  { value: 'economic', label: '経済的リスク', description: 'バリュエーション、シナジー効果等' },
  { value: 'financial', label: '財政的リスク', description: '簿外債務、偶発債務等' },
  { value: 'international', label: '国際的リスク', description: '外為法、海外規制等' },
  { value: 'domestic_political', label: '国内政治的リスク', description: '業界規制、政策変更等' },
  { value: 'reputation', label: '対世論リスク', description: 'メディア対応、ステークホルダー対応等' },
  { value: 'employee', label: '従業員リスク', description: '雇用承継、労働条件等' },
  { value: 'labor_union', label: '労働組合リスク', description: '労働組合との協議等' },
];

interface FormData {
  name: string;
  description: string;
  maMethod: MAMethod | '';
  targetCompany: string;
  targetCountry: Country;
  targetIndustry: string;
  dealSize: string;
  selectedRiskCategories: RiskCategory[];
}

export function ProjectCreateEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    maMethod: '',
    targetCompany: '',
    targetCountry: 'japan',
    targetIndustry: '',
    dealSize: '',
    selectedRiskCategories: ['legal', 'economic', 'financial'],
  });

  // Load existing project data in edit mode
  const loadProject = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const project = await projectService.getProject(id);
      setFormData({
        name: project.name,
        description: project.description || '',
        maMethod: project.maMethod,
        targetCompany: project.targetCompany,
        targetCountry: project.targetCountry,
        targetIndustry: '', // APIにはこのフィールドがないため空
        dealSize: '', // APIにはこのフィールドがないため空
        selectedRiskCategories: ['legal', 'economic', 'financial'], // デフォルト値
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : '案件データの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditMode && id) {
      loadProject();
    }
  }, [isEditMode, id, loadProject]);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleRiskCategoryToggle = (category: RiskCategory) => {
    setFormData((prev) => ({
      ...prev,
      selectedRiskCategories: prev.selectedRiskCategories.includes(category)
        ? prev.selectedRiskCategories.filter((c) => c !== category)
        : [...prev.selectedRiskCategories, category],
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (!formData.maMethod) {
        throw new Error('M&A手法を選択してください');
      }

      if (isEditMode && id) {
        // 案件更新
        await projectService.updateProject(id, {
          name: formData.name,
          maMethod: formData.maMethod,
          targetCompany: formData.targetCompany,
          targetCountry: formData.targetCountry,
          description: formData.description || undefined,
        });
        navigate(`/projects/${id}`);
      } else {
        // 案件新規作成
        const newProject = await projectService.createProject({
          name: formData.name,
          maMethod: formData.maMethod,
          targetCompany: formData.targetCompany,
          targetCountry: formData.targetCountry,
          description: formData.description || undefined,
        });
        navigate(`/projects/${newProject.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0: // 基本情報
        return !!formData.name;
      case 1: // M&A手法
        return !!formData.maMethod;
      case 2: // 対象企業情報
        return !!formData.targetCompany;
      case 3: // リスクカテゴリ
        return formData.selectedRiskCategories.length > 0;
      case 4: // 確認
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0: // 基本情報
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="案件名"
              placeholder="例: 株式会社ABC 株式譲渡案件"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              helperText="案件を識別するための名称を入力してください"
            />
            <TextField
              label="案件概要"
              placeholder="案件の背景や目的を記入してください"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={4}
            />
          </Box>
        );

      case 1: // M&A手法
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              M&A手法を選択すると、関連するチェック項目が自動的に適用されます。
            </Typography>
            {maMethodOptions.map((option) => (
              <Paper
                key={option.value}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: formData.maMethod === option.value ? 'primary.main' : 'transparent',
                  bgcolor: formData.maMethod === option.value ? 'primary.50' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => setFormData({ ...formData, maMethod: option.value })}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        );

      case 2: // 対象企業情報
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="対象企業名"
              placeholder="例: 株式会社ABC"
              value={formData.targetCompany}
              onChange={(e) => setFormData({ ...formData, targetCompany: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>対象国・地域</InputLabel>
              <Select
                value={formData.targetCountry}
                label="対象国・地域"
                onChange={(e) => setFormData({ ...formData, targetCountry: e.target.value as Country })}
              >
                {countryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="業種"
              placeholder="例: 製造業"
              value={formData.targetIndustry}
              onChange={(e) => setFormData({ ...formData, targetIndustry: e.target.value })}
              fullWidth
            />
            <TextField
              label="想定取引規模"
              placeholder="例: 50億円"
              value={formData.dealSize}
              onChange={(e) => setFormData({ ...formData, dealSize: e.target.value })}
              fullWidth
            />
          </Box>
        );

      case 3: // リスクカテゴリ選択
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              チェック対象とするリスクカテゴリを選択してください。
              選択したカテゴリに関連するチェック項目が案件に適用されます。
            </Typography>
            <FormGroup>
              {riskCategoryOptions.map((option) => (
                <Paper
                  key={option.value}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: 1,
                    borderColor: formData.selectedRiskCategories.includes(option.value)
                      ? 'primary.main'
                      : 'divider',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.selectedRiskCategories.includes(option.value)}
                        onChange={() => handleRiskCategoryToggle(option.value)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle2">{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              ))}
            </FormGroup>
          </Box>
        );

      case 4: // 確認
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                入力内容の確認
              </Typography>

              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    案件名
                  </Typography>
                  <Typography variant="body1">{formData.name || '-'}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    案件概要
                  </Typography>
                  <Typography variant="body1">{formData.description || '-'}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    M&A手法
                  </Typography>
                  <Typography variant="body1">
                    {maMethodOptions.find((m) => m.value === formData.maMethod)?.label || '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    対象企業
                  </Typography>
                  <Typography variant="body1">
                    {formData.targetCompany || '-'}（
                    {countryOptions.find((c) => c.value === formData.targetCountry)?.label}）
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    業種
                  </Typography>
                  <Typography variant="body1">{formData.targetIndustry || '-'}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    想定取引規模
                  </Typography>
                  <Typography variant="body1">{formData.dealSize || '-'}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    選択リスクカテゴリ
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {formData.selectedRiskCategories.map((cat) => (
                      <Chip
                        key={cat}
                        label={riskCategoryOptions.find((r) => r.value === cat)?.label}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Typography variant="h5" component="h1" fontWeight={500} sx={{ mb: 3 }}>
          {isEditMode ? '案件編集' : '新規案件作成'}
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Paper sx={{ p: 3, mb: 3 }}>{renderStepContent(activeStep)}</Paper>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={activeStep === 0 ? () => navigate(-1) : handleBack}
          >
            {activeStep === 0 ? 'キャンセル' : '戻る'}
          </Button>

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isEditMode ? '更新' : '作成'}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
            >
              次へ
            </Button>
          )}
        </Box>
      </Box>
    </MainLayout>
  );
}
